import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { DashboardPageFormBody } from '@shigo-ui/dashboard-ui';

import { ChoiceCardField } from '../../../../../components/forms/ChoiceCardField';
import {
  fallbackDemandPartnerBidderConfig,
  resolveDemandPartnerBidderConfig,
} from '../../bidderConfig';
import type { DemandPartnerFormValue } from '../../formSchema';
import { DemandPartnerDynamicField, DemandPartnerPricingType } from '../../model';
import { useDemandPartnerBidderConfigQuery } from '../../hooks';
import { FormSwitchField } from './FormSwitchField';
import { GrossNetField } from './GrossNetField';
import { DemandPartnerNumberField } from './DemandPartnerNumberField';

/** Form-only "no cost" choice; it serializes as a null `pricingType`. */
enum DemandPartnerPricingChoice {
  NoCost = 'NO_COST',
}

const pricingOptions = [
  {
    value: DemandPartnerPricingChoice.NoCost,
    label: 'No cost',
    description: 'The demand partner takes no share.',
  },
  {
    value: DemandPartnerPricingType.RevShare,
    label: 'Rev Share',
    description: 'The demand partner takes a percentage of revenue.',
  },
  {
    value: DemandPartnerPricingType.ProfitShare,
    label: 'Profit Share',
    description: 'The demand partner takes a percentage of profit.',
  },
] as const;

export function DemandPartnerPricingFields() {
  const { control, setValue } = useFormContext<DemandPartnerFormValue>();
  const pricingType = useWatch({ control, name: 'pricingType' });
  const bidderCode = useWatch({ control, name: 'bidderCode' });
  const configQuery = useDemandPartnerBidderConfigQuery();
  const dynamic = useMemo(
    () =>
      resolveDemandPartnerBidderConfig(
        bidderCode,
        configQuery.data ?? fallbackDemandPartnerBidderConfig,
      ),
    [bidderCode, configQuery.data],
  );
  return (
    <DashboardPageFormBody width="lg" title="Pricing">
      <ChoiceCardField
        label="Pricing model"
        value={pricingType ?? DemandPartnerPricingChoice.NoCost}
        options={pricingOptions}
        onValueChange={(value) => {
          setValue('pricingType', value === DemandPartnerPricingChoice.NoCost ? null : value, {
            shouldDirty: true,
          });
        }}
      />
      <div className="grid gap-5 md:grid-cols-2">
        <GrossNetField name="revShareGrossNet" label="Reporting Gross / Net" />
        {pricingType === DemandPartnerPricingType.RevShare ||
        pricingType === DemandPartnerPricingType.ProfitShare ? (
          <DemandPartnerNumberField
            name="revShareValue"
            label="Share (%)"
            required
            hint={`${pricingType === DemandPartnerPricingType.ProfitShare ? 'Profit share' : 'Rev Share'} is demand partner’s share. e.g. if demand partner's share is 20% please enter 20`}
            suffix="%"
            min={0}
            step={0.01}
          />
        ) : null}
        {dynamic.visible.includes(DemandPartnerDynamicField.AdditionalCost) ? (
          <DemandPartnerNumberField
            name="additionalCost"
            label="Additional cost"
            required={dynamic.required.includes(DemandPartnerDynamicField.AdditionalCost)}
            prefix="$"
            min={0}
            step={0.01}
          />
        ) : null}
        {dynamic.visible.includes(DemandPartnerDynamicField.UseStickyPricing) ? (
          <FormSwitchField
            name="useStickyPricing"
            label="Sticky pricing"
            hint="Used if pricing isn't present in vast."
          />
        ) : null}
      </div>
    </DashboardPageFormBody>
  );
}

export function DemandPartnerBiddingFields() {
  return (
    <DashboardPageFormBody
      title="Bidding"
      description="Configure the bidding basis and timing limits."
    >
      <div className="grid gap-5 md:grid-cols-2">
        <GrossNetField
          name="biddingGrossNet"
          label="Bidding Gross / Net"
          hint="Required value used when evaluating incoming bids."
        />
        <DemandPartnerNumberField
          name="biddingTmax"
          label="Timeout (ms)"
          hint="Maximum bidder response time in milliseconds."
          suffix="ms"
          min={0}
        />
        <DemandPartnerNumberField
          name="biddingBidTTL"
          label="Bid TTL (seconds)"
          hint="How long a bid remains valid, in seconds."
          suffix="s"
          min={0}
        />
      </div>
    </DashboardPageFormBody>
  );
}
